import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Info, Calculator, CheckCircle2 } from 'lucide-react';
export function PricingCalculator() {
  const [systemType, setSystemType] = useState<'split' | 'multi' | 'ducted'>('ducted');
  const [size, setSize] = useState<'small' | 'medium' | 'large' | 'xlarge'>('medium');
  const homeSizes = {
    small: {
      label: '1 to 2 bed',
      sqm: '60 to 110 sqm'
    },
    medium: {
      label: '3 bedroom',
      sqm: '120 to 180 sqm'
    },
    large: {
      label: '4 bedroom',
      sqm: '180 to 260 sqm'
    },
    xlarge: {
      label: '5+ bedroom',
      sqm: '260+ sqm'
    }
  };
  const systems = {
    split: {
      label: 'Single split',
      desc: 'One room'
    },
    multi: {
      label: 'Multi split',
      desc: '2 to 4 rooms'
    },
    ducted: {
      label: 'Ducted',
      desc: 'Whole home'
    }
  };
  const estimate = useMemo(() => {
    const ranges: Record<string, Record<string, [number, number]>> = {
      split: {
        small: [2500, 3800],
        medium: [3200, 4800],
        large: [4500, 6500],
        xlarge: [5500, 8000]
      },
      multi: {
        small: [5500, 8500],
        medium: [7500, 12000],
        large: [10500, 15500],
        xlarge: [13000, 18000]
      },
      ducted: {
        small: [10000, 13000],
        medium: [13000, 16500],
        large: [16500, 20000],
        xlarge: [18000, 24000]
      }
    };
    return ranges[systemType][size];
  }, [systemType, size]);
  const monthlyFinance = Math.round(estimate[0] / 60 / 5) * 5;
  return <section id="pricing" className="bg-white py-20 md:py-28 scroll-mt-24" data-id="element-462">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-463">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16" data-id="element-464">
          {/* Left column */}
          <div className="lg:col-span-5" data-id="element-465">
            <div className="text-xs font-semibold uppercase tracking-widest text-brand mb-4" data-id="element-466">
              Real Perth Prices
            </div>
            <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-ink leading-tight mb-6" data-id="element-467">
              How much will it actually cost?
            </h2>
            <p className="text-lg text-stone-600 leading-relaxed mb-8 max-w-md" data-id="element-468">
              Most installers hide their numbers. We publish ours. Pick your
              system and home size for a realistic Perth installed range.
            </p>

            <div className="space-y-4 text-sm text-stone-700" data-id="element-469">
              <div className="flex gap-3" data-id="element-470">
                <CheckCircle2 size={18} className="text-success flex-shrink-0 mt-0.5" data-id="element-471" />
                <p data-id="element-472">
                  <strong className="text-ink" data-id="element-473">What is included:</strong> Unit,
                  indoor and outdoor install, all electrical, mounting hardware,
                  removal and disposal of old gear, commissioning and
                  walkthrough.
                </p>
              </div>
              <div className="flex gap-3" data-id="element-474">
                <CheckCircle2 size={18} className="text-success flex-shrink-0 mt-0.5" data-id="element-475" />
                <p data-id="element-476">
                  <strong className="text-ink" data-id="element-477">Fixed price promise:</strong>{' '}
                  Once Mike quotes on site, that is the price. No variations on
                  install day.
                </p>
              </div>
              <div className="flex gap-3" data-id="element-478">
                <CheckCircle2 size={18} className="text-success flex-shrink-0 mt-0.5" data-id="element-479" />
                <p data-id="element-480">
                  <strong className="text-ink" data-id="element-481">Finance available:</strong>{' '}
                  Interest free options through Brighte and humm for jobs over
                  $5,000.
                </p>
              </div>
            </div>

            <div className="mt-8 p-4 bg-brand-50 rounded-2xl border border-brand/10" data-id="element-482">
              <p className="text-sm text-ink leading-relaxed" data-id="element-483">
                <strong data-id="element-484">Anchor point:</strong> A wrong sized unit can cost $400+
                extra per year in power and fail years early. Mike sizes every
                job on site, personally.
              </p>
            </div>
          </div>

          {/* Right column: calculator */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-7" data-id="element-485">
            <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-10 border border-stone-200 shadow-xl" data-id="element-486">
              <div className="flex items-center gap-2 mb-7" data-id="element-487">
                <Calculator size={18} className="text-brand" data-id="element-488" />
                <span className="text-xs font-semibold uppercase tracking-widest text-stone-700" data-id="element-489">
                  Cost Estimator
                </span>
              </div>

              {/* System type */}
              <div className="mb-7" data-id="element-490">
                <label className="block text-sm font-semibold text-ink mb-3" data-id="element-491">
                  What kind of system?
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-492">
                  {Object.entries(systems).map(([key, sys]) => <button key={key} onClick={() => setSystemType(key as 'split' | 'multi' | 'ducted')} className={`text-left p-3 rounded-xl border-2 transition-all ${systemType === key ? 'bg-brand text-white border-brand' : 'bg-white border-stone-200 hover:border-brand/40 text-ink'}`} data-id="element-493">
                      <div className="font-semibold text-sm leading-tight" data-id="element-494">
                        {sys.label}
                      </div>
                      <div className={`text-xs mt-1 ${systemType === key ? 'text-white/80' : 'text-stone-500'}`} data-id="element-495">
                        {sys.desc}
                      </div>
                    </button>)}
                </div>
              </div>

              {/* Home size */}
              <div className="mb-8" data-id="element-496">
                <label className="block text-sm font-semibold text-ink mb-3" data-id="element-497">
                  Home size?
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-498">
                  {Object.entries(homeSizes).map(([key, hs]) => <button key={key} onClick={() => setSize(key as 'small' | 'medium' | 'large' | 'xlarge')} className={`p-3 rounded-xl border-2 transition-all text-left ${size === key ? 'bg-brand text-white border-brand' : 'bg-white border-stone-200 hover:border-brand/40 text-ink'}`} data-id="element-499">
                      <div className="font-semibold text-sm leading-tight" data-id="element-500">
                        {hs.label}
                      </div>
                      <div className={`text-xs mt-1 ${size === key ? 'text-white/80' : 'text-stone-500'}`} data-id="element-501">
                        {hs.sqm}
                      </div>
                    </button>)}
                </div>
              </div>

              {/* Result */}
              <motion.div key={`${systemType}-${size}`} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3
            }} className="bg-brand text-white rounded-2xl p-6 sm:p-7" data-id="element-502">
                <div className="text-xs uppercase tracking-widest text-white/70 font-semibold mb-3" data-id="element-503">
                  Typical Range, Fully Installed
                </div>
                <div className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-none tabular-nums" data-id="element-504">
                  A${estimate[0].toLocaleString()}
                  <span className="text-white/50" data-id="element-505"> to </span>
                  A${estimate[1].toLocaleString()}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/85" data-id="element-506">
                  <span className="flex items-center gap-1.5" data-id="element-507">
                    <Info size={13} className="text-accent" data-id="element-508" />
                    Or from ~${monthlyFinance}/month on interest free finance
                  </span>
                </div>

                <a href="#quote-form" className="mt-6 inline-flex items-center gap-2 px-6 h-12 rounded-xl bg-accent hover:bg-accent-600 text-ink text-sm font-bold transition-all hover:scale-[1.02] shadow-lg shadow-accent/20" data-id="element-509">
                  Get my exact fixed quote
                  <ArrowRight size={15} strokeWidth={2.5} data-id="element-510" />
                </a>
              </motion.div>

              <p className="mt-4 text-xs text-stone-500 leading-relaxed" data-id="element-511">
                Estimates based on real Perth installs over the past 12 months.
                Your exact quote depends on home layout, electrical access and
                brand chosen. Free on site visit, no obligation.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}